import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(isoWeek);

export function groupSchedulesByTimeframe(schedules, now = dayjs()) {
  const buckets = new Map();

  const today = now.startOf('day');
  const tomorrow = today.add(1, 'day');
  const thisWeekStart = now.startOf('week');
  const nextWeekStart = thisWeekStart.add(1, 'week');

  const formatWeekLabel = (start) => {
    const format = start.isSame(now, 'year') ? 'MMM D' : 'MMM D, YYYY';
    return `Week of ${start.format(format)}`;
  };

  function ensure(key, label, sortValue) {
    if (!buckets.has(key)) {
      buckets.set(key, { key, label, items: [], sortValue });
    }
    return buckets.get(key);
  }

  for (const item of schedules) {
    if (item?.isFiring) {
      ensure('now', 'Now', Number.NEGATIVE_INFINITY).items.push(item);
      continue;
    }

    const dt = dayjs(item?.nextRunTime);
    if (!dt.isValid()) {
      ensure('unparsed', 'Scheduling it...', Number.POSITIVE_INFINITY).items.push(item);
      continue;
    }

    let key;
    let label;
    let sortValue;

    if (dt.isSame(today, 'day')) {
      key = 'today';
      label = 'Today';
      sortValue = today.valueOf();
    } else if (dt.isSame(tomorrow, 'day')) {
      key = 'tomorrow';
      label = 'Tomorrow';
      sortValue = tomorrow.valueOf();
    } else if (dt.isSame(thisWeekStart, 'week')) {
      key = 'this-week';
      label = 'This week';
      sortValue = thisWeekStart.valueOf();
    } else if (dt.isSame(nextWeekStart, 'week')) {
      key = 'next-week';
      label = 'Next week';
      sortValue = nextWeekStart.valueOf();
    } else {
      const start = dt.startOf('week');
      key = `week-${start.format('YYYY-MM-DD')}`;
      label = formatWeekLabel(start);
      sortValue = start.valueOf();
    }

    ensure(key, label, sortValue).items.push(item);
  }

  const order = ['now', 'today', 'tomorrow', 'this-week', 'next-week'];

  const sorted = [
    ...order.map((k) => buckets.get(k)).filter(Boolean),
    ...[...buckets.values()]
      .filter((bucket) => !order.includes(bucket.key))
      .sort((a, b) => {
        const aSort = a.sortValue ?? Number.POSITIVE_INFINITY;
        const bSort = b.sortValue ?? Number.POSITIVE_INFINITY;
        if (aSort !== bSort) return aSort - bSort;
        return a.label.localeCompare(b.label);
      }),
  ];

  return sorted.map((group) => {
    const { items, sortValue, ...rest } = group;
    return {
      ...rest,
      items: [...items].sort((a, b) => {
        const af = a?.isFiring ? 1 : 0;
        const bf = b?.isFiring ? 1 : 0;
        if (af !== bf) return bf - af;
        const at = new Date(a.nextRunTime).getTime();
        const bt = new Date(b.nextRunTime).getTime();
        if (at !== bt) return at - bt;
        const ac = new Date(a.createdDate).getTime();
        const bc = new Date(b.createdDate).getTime();
        return bc - ac;
      }),
    };
  });
}
